import { useState, useRef, useEffect, useCallback } from "react";
import { Star, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { reviews, googleReviewSummary, type Review } from "@/data/reviews";

const AVERAGE = googleReviewSummary.rating.toFixed(1);
const TOTAL = googleReviewSummary.reviewCount;
const SPEED = 26; // px per second, slow premium drift
const LONG_TEXT = 200;

const GoogleIcon = ({ className = "w-5 h-5 shrink-0" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const Stars = ({ rating }: { rating: number }) => (
  <div className="flex gap-0.5" role="img" aria-label={`${rating} out of 5 stars`}>
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-[18px] h-[18px] ${
          i < rating ? "fill-[#FDC00E] text-[#FDC00E]" : "fill-[#1a1a1a]/10 text-[#1a1a1a]/10"
        }`}
      />
    ))}
  </div>
);

const ReviewCard = ({ review }: { review: Review }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = review.text.length > LONG_TEXT;

  return (
    <article className="h-full bg-white rounded-2xl border border-[#1a1a1a]/10 shadow-[0_2px_12px_rgba(0,0,0,0.04)] p-7 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <Stars rating={review.rating} />
        <GoogleIcon className="w-5 h-5 shrink-0" />
      </div>

      <p
        className={`text-[#1a1a1a]/75 leading-relaxed text-[15px] ${
          expanded || !isLong ? "" : "line-clamp-5"
        }`}
      >
        {review.text}
      </p>

      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="self-start mt-3 text-sm font-bold text-[#1a1a1a] underline underline-offset-4 hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a1a1a] rounded"
          aria-expanded={expanded}
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}

      <div className="mt-6 pt-5 border-t border-[#1a1a1a]/10 flex items-center gap-3">
        {review.image ? (
          <img
            src={review.image}
            alt={`${review.name} profile photo`}
            loading="lazy"
            decoding="async"
            width={40}
            height={40}
            className="w-10 h-10 rounded-full object-cover shrink-0"
          />
        ) : (
          <span
            aria-hidden="true"
            className="w-10 h-10 rounded-full bg-[#FDC00E] text-[#1a1a1a] font-black flex items-center justify-center shrink-0"
          >
            {review.name.trim().charAt(0).toUpperCase()}
          </span>
        )}
        <div className="min-w-0">
          <p className="font-bold text-sm text-[#1a1a1a] truncate">{review.name}</p>
          <p className="text-xs text-[#1a1a1a]/60">{review.date}</p>
        </div>
        {review.googleUrl && (
          <a
            href={review.googleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-[#1a1a1a]/70 hover:text-[#1a1a1a] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a1a1a] rounded"
          >
            View on Google <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    </article>
  );
};

const ReviewsSection = () => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const targetRef = useRef<number | null>(null);
  const halfRef = useRef(0);
  const pausedRef = useRef(false);
  const draggingRef = useRef(false);
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [reduced, setReduced] = useState(false);

  const loop = [...reviews, ...reviews];

  // measure half-track width (one full copy of the list)
  const measure = useCallback(() => {
    if (!trackRef.current) return;
    halfRef.current = trackRef.current.scrollWidth / 2;
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    measure();
    let t: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(measure, 150);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(t);
    };
  }, [measure]);

  // rAF driven transform (no interval/timer scrolling)
  useEffect(() => {
    let raf = 0;
    let last = performance.now();

    const tick = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      const half = halfRef.current;

      if (half > 0) {
        if (targetRef.current !== null) {
          const diff = targetRef.current - offsetRef.current;
          if (Math.abs(diff) < 0.5) {
            offsetRef.current = targetRef.current;
            targetRef.current = null;
          } else {
            offsetRef.current += diff * Math.min(1, dt * 8);
          }
        } else if (!pausedRef.current && !draggingRef.current && !reduced) {
          offsetRef.current -= SPEED * dt;
        }

        // seamless wrap
        while (offsetRef.current <= -half) {
          offsetRef.current += half;
          if (targetRef.current !== null) targetRef.current += half;
        }
        while (offsetRef.current > 0) {
          offsetRef.current -= half;
          if (targetRef.current !== null) targetRef.current -= half;
        }
      }

      if (trackRef.current) {
        trackRef.current.style.transform = `translate3d(${offsetRef.current}px,0,0)`;
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reduced]);

  const scheduleResume = useCallback(() => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => {
      pausedRef.current = false;
    }, 3000);
  }, []);

  useEffect(() => () => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
  }, []);

  const step = useCallback(
    (dir: 1 | -1) => {
      const card = trackRef.current?.querySelector<HTMLElement>("[data-review-card]");
      const amount = card ? card.offsetWidth + 24 : 320;
      const base = targetRef.current ?? offsetRef.current;
      targetRef.current = base - dir * amount;
      pausedRef.current = true;
      scheduleResume();
    },
    [scheduleResume]
  );

  // drag / swipe
  const startX = useRef(0);
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    draggingRef.current = true;
    targetRef.current = null;
    startX.current = e.clientX;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - startX.current;
    startX.current = e.clientX;
    offsetRef.current += dx;
  };
  const endDrag = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    pausedRef.current = true;
    scheduleResume();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      step(1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      step(-1);
    }
  };

  return (
    <section id="reviews" className="scroll-mt-28 bg-white py-20 md:py-28 overflow-hidden">
      <div className="container">
        {/* Header */}
        <div className="mb-12 md:mb-14">
          <p className="flex items-center gap-2 text-xs md:text-sm font-bold uppercase tracking-[0.18em] text-[#1a1a1a]/60 mb-3">
            <GoogleIcon className="w-4 h-4 shrink-0" /> Reviews from OZ MOVE CO. customers
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-[#1a1a1a] leading-tight">
            Backed by the Team Behind OZ MOVE CO.
          </h2>
          <p className="text-[#1a1a1a]/70 mt-2 text-base">
            OZ WASTE CO. is built by the same Melbourne team focused on reliable service, clear communication and doing the job properly.
          </p>


          {/* Compact Google rating summary */}
          <div className="flex items-center gap-5 mt-6">
            <span className="text-5xl font-black text-[#1a1a1a] leading-none tracking-tight">
              {AVERAGE}
            </span>
            <div className="flex flex-col gap-1.5">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#FDC00E] text-[#FDC00E]" />
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-sm font-bold text-[#1a1a1a]/70">
                  {TOTAL}&nbsp;reviews
                </span>
                <span className="flex items-center gap-1.5 text-xs text-[#1a1a1a]/70">
                  <GoogleIcon />
                  Based on Google Reviews
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Carousel */}
        <div
          ref={viewportRef}
          role="region"
          aria-label="Customer reviews carousel"
          tabIndex={0}
          onKeyDown={onKeyDown}
          onMouseEnter={() => {
            pausedRef.current = true;
          }}
          onMouseLeave={() => {
            pausedRef.current = false;
          }}
          onFocus={() => {
            pausedRef.current = true;
          }}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) pausedRef.current = false;
          }}
          className="relative overflow-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#1a1a1a] rounded-2xl"
        >
          <div
            ref={trackRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            className="flex gap-6 will-change-transform cursor-grab active:cursor-grabbing touch-pan-y select-none py-1"
          >
            {loop.map((r, i) => (
              <div
                key={`${r.name}-${i}`}
                data-review-card
                aria-hidden={i >= reviews.length}
                className="shrink-0 w-full sm:w-[calc(50%-12px)] lg:w-[calc((100%-48px)/3)]"
              >
                <ReviewCard review={r} />
              </div>
            ))}
          </div>
        </div>

        {/* Arrows */}
        <div className="flex justify-center gap-3 mt-8">
          <button
            type="button"
            onClick={() => step(-1)}
            aria-label="Previous reviews"
            className="w-12 h-12 rounded-full bg-white border border-[#1a1a1a]/15 flex items-center justify-center shadow-sm hover:shadow-md transition-shadow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a1a1a]"
          >
            <ChevronLeft className="w-5 h-5 text-[#1a1a1a]" />
          </button>
          <button
            type="button"
            onClick={() => step(1)}
            aria-label="Next reviews"
            className="w-12 h-12 rounded-full bg-white border border-[#1a1a1a]/15 flex items-center justify-center shadow-sm hover:shadow-md transition-shadow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a1a1a]"
          >
            <ChevronRight className="w-5 h-5 text-[#1a1a1a]" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
